import api, {normalizeApiError} from "../../system/api";

export const AI_ERROR_CODES = {
    NO_WORKSPACE: "AI_NO_WORKSPACE",
    BAD_REQUEST: "AI_BAD_REQUEST",
    UNAUTHORIZED: "AI_UNAUTHORIZED",
    FORBIDDEN: "AI_FORBIDDEN",
    RATE_LIMITED: "AI_RATE_LIMITED",
    BAD_GATEWAY: "AI_BAD_GATEWAY",
    GENERIC: "AI_GENERIC",
};

export default class Ai {
    constructor(workspaceId) {
        this.workspaceId = workspaceId;
    }

    async request(method, path, {workspaceId = this.workspaceId, data, params, suppressGlobalErrorHandler = false} = {}) {
        if (!workspaceId) throw createAiError(AI_ERROR_CODES.NO_WORKSPACE);
        try {
            const response = await api.request({
                method,
                url: path,
                data: data ? {...data, workspaceId} : undefined,
                params,
                headers: {"Content-Type": "application/json", workspace: workspaceId},
                suppressGlobalErrorHandler,
            });
            return response.data?.data;
        } catch (error) {
            const normalized = error?.normalizedError || normalizeApiError(error);
            throw createAiError(normalized.code || mapStatusToCode(normalized.status), normalized);
        }
    }

    async chat({workspaceId, message}) {
        const data = await this.request("post", "/ai/chat", {workspaceId, data: {message}});
        return {data};
    }

    askPage(data) { return this.request("post", "/ai/page/ask", {data}); }
    generateCreationDraft(data) { return this.request("post", "/ai/action-drafts/generate", {data}); }
    suggestions(pageType) { return this.request("get", "/ai/page/suggestions", {params: {pageType}}); }
    conversations(params = {}) { return this.request("get", "/ai/conversations", {params}); }
    conversationMessages(documentId, params = {}) {
        return this.request("get", `/ai/conversations/${documentId}/messages`, {params});
    }
    commandCenter(context = {}) {
        const params = {
            ...context,
            selectedEntityIds: context.selectedEntityIds ? JSON.stringify(context.selectedEntityIds) : undefined,
            filters: context.filters ? JSON.stringify(context.filters) : undefined,
        };
        return this.request("get", "/ai/command-center", {params});
    }
    insights(params = {}) { return this.request("get", "/ai/insights", {params}); }
    forecasts(params = {}) { return this.request("get", "/ai/forecasts", {params}); }
    recommendations(params = {}) { return this.request("get", "/ai/recommendations", {params}); }
    runAnalysis() { return this.request("post", "/ai/analysis/run", {data: {}}); }
    usage() { return this.request("get", "/ai/usage/summary"); }
    settings() { return this.request("get", "/ai/settings"); }
    updateSettings(data) { return this.request("put", "/ai/settings", {data}); }
    decideRecommendation(documentId, data) { return this.request("post", `/ai/recommendations/${documentId}/decision`, {data}); }
    createRecommendationProposal(documentId, data = {}) { return this.request("post", `/ai/recommendations/${documentId}/proposal`, {data}); }
    createProposal(data = {}) { return this.request("post", "/ai/proposals", {data}); }
    proposal(documentId) { return this.request("get", `/ai/proposals/${documentId}`); }
    validateProposal(documentId) { return this.request("post", `/ai/proposals/${documentId}/validate`, {data: {}}); }
    submitProposal(documentId) { return this.request("post", `/ai/proposals/${documentId}/submit`, {data: {}}); }
    approveProposal(documentId, data = {}) { return this.request("post", `/ai/proposals/${documentId}/approve`, {data}); }
    rejectProposal(documentId, data = {}) { return this.request("post", `/ai/proposals/${documentId}/reject`, {data}); }
    executeProposal(documentId, idempotencyKey) {
        return this.request("post", `/ai/proposals/${documentId}/execute`, {data: {idempotencyKey}});
    }
    cancel(runDocumentId) { return this.request("post", `/ai/runs/${runDocumentId}/cancel`, {data: {}}); }
    feedback(messageDocumentId, rating, reason) {
        return this.request("post", `/ai/messages/${messageDocumentId}/feedback`, {data: {rating, reason}});
    }
}

function createAiError(code, details) {
    const error = new Error(details?.message || code);
    error.code = code;
    error.details = details;
    error.status = details?.status;
    error.retryable = Boolean(details?.retryable);
    error.runId = details?.runId || null;
    error.stepId = details?.stepId || null;
    return error;
}

function mapStatusToCode(status) {
    switch (status) {
        case 400: return AI_ERROR_CODES.BAD_REQUEST;
        case 401: return AI_ERROR_CODES.UNAUTHORIZED;
        case 403: return AI_ERROR_CODES.FORBIDDEN;
        case 429: return AI_ERROR_CODES.RATE_LIMITED;
        case 502: return AI_ERROR_CODES.BAD_GATEWAY;
        default: return AI_ERROR_CODES.GENERIC;
    }
}
